/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import React from "react";
import { TextField } from "@mui/material";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTime } from "luxon";
import { useTranslation } from "../../Internationalisierung/i18nBasis";
import { cultures } from "../../Internationalisierung/languages";
import AnyUtil from "../../Utils/AnyUtil";
import useErrors from "../../Utils/userErrors";
import ErrorText from "../ErrorText";
import withTooltip from "../HOC/withTooltip";
import DateTimeInputProps from "./DateTimeInputProps";

const DATE_TIME_FORMAT = Object.freeze({
    INPUT: "dd.MM.yyyy HH:mm",
    MASK: "__.__.____ __:__",
    PLACEHOLDER: "tt.mm.jjjj hh:mm",
});

export default withTooltip(function DateTimeInput(props: DateTimeInputProps) {
    const { error, errorTexts, clearErrorsOnChange } = useErrors(props);
    const { tValue } = useTranslation();

    function renderStartAndornment() {
        const date = DateTime.fromISO(props.value ?? "");
        if (!date.isValid) {
            return null;
        }

        return <span>{`${tValue(props.value, "Weekday")}`}&nbsp;</span>
    }

    function handleAccept(value: DateTime | null) {
        handleBlur(toDateTimeString(value));
        handleChange(value);
    }

    function handleBlur(value?: string) {
        if (!value) {
            props.onBlur?.("");
            return;
        }

        if (asDateTime(value)?.isValid) {
            props.onBlur?.(value);
            return;
        }

        props.onBlur?.(props.value ?? "")
    }

    function handleChange(value: DateTime | null, keyboardInputValue?: string) {
        if (value && value.isValid) {
            props.onChange?.(toDateTimeString(value));
            return;
        }

        if (!keyboardInputValue) {
            props.onChange?.("");
        }
    }

    function adaptedValue() {
        if (props.value === undefined || props.value === null || props.value === "") {
            return null;
        }

        if (props.isDateTime) {
            return toDateTimeString(asDateTime(props.value));
        }

        return props.value;
    }

    function asDateTime(value?: string | null ) {
        if (AnyUtil.isNullOrUndefined(value)) {
            return null;
        }

        if (props.isDateTime) {
            // Luxon rechnet das gelieferte Datum automatisch in die Zeitzone des Clients um.
            // Um dies zu verhindern, rechnen wir bei DateTime (Offset irrelevant)
            // das DateTime in die Zeit minus dem aktuellen Offset der Zeitzone um.
            const datetime = DateTime.fromISO(value, { zone: "utc", setZone: true });
            const offset = DateTime.local().zone.offset(datetime.toMillis()) / 60;
            return datetime.minus({ hours: offset });
        }

        return DateTime.fromISO(value);
    }

    function toDateTimeString(value: DateTime | null) {
        if (value === null) {
            return "";
        }

        if (props.isDateTime) {
            // Wenn ein DateTime (ohne Offset) gespeichert wird, müssen wir das Offset verwerfen
            // Das liegt daran, dass die Anzeige im UI richtig sein muss
            value = value.setZone("utc", { keepLocalTime: true });
        }

        value = value.minus(value.second * 1000 + value.millisecond);
        return value.toString();
    }

    return (
        <LocalizationProvider adapterLocale={cultures.deDE} dateAdapter={AdapterLuxon}>
            <DateTimePicker<string, DateTime>
                renderInput={params => (
                    <TextField
                        {...params}
                        required={props.required}
                        onBlur={e => handleBlur(e.target.value)}
                        error={error}
                        helperText={error ? <ErrorText errors={errorTexts} /> : undefined}
                        InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
                        inputProps={{ ...params.inputProps, placeholder: DATE_TIME_FORMAT.PLACEHOLDER }}
                        sx={{ mt: 1, mb: 0.5, ...(props.sx ?? {}) }}
                        size="small"
                        autoComplete="off"
                        fullWidth
                        margin={props.margin}
                        autoFocus={props.autoFocus}
                    />
                )}
                autoFocus={props.autoFocus}
                value={adaptedValue()}
                label={props.caption}
                onAccept={handleAccept}
                onChange={clearErrorsOnChange(handleChange)}
                inputFormat={DATE_TIME_FORMAT.INPUT}
                mask={DATE_TIME_FORMAT.MASK}
                disabled={props.readonly}
                InputProps={{ startAdornment: renderStartAndornment() }}
                disableOpenPicker={props.disableOpenPicker}
            />
        </LocalizationProvider>
    )
});
